// API service for backend communication
import { auth } from "../firebase/firebase";

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_API || "http://localhost:3000/api/v1/";

// Helper to get the base URL for static files (images, etc.)
export const STATIC_BASE_URL = API_BASE_URL.replace("/api/v1/", "");

class ApiService {
  // Helper method to get authenticated headers
  static async getAuthHeaders() {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
    }
    return {
      "Content-Type": "application/json",
    };
  }

  // Helper method to get full image URL
  static getImageUrl(imagePath) {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath; // Already a full URL
    return STATIC_BASE_URL + imagePath;
  }

  static async registerUser(userData) {
    try {
      const response = await fetch(`${API_BASE_URL}users/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Registration failed");
      }

      return await response.json();
    } catch (error) {
      console.error("API Registration Error:", error);
      throw error;
    }
  }
  static async getUserProfile(firebaseUid) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}users/${firebaseUid}`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch user profile");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Profile Error:", error);
      throw error;
    }
  }
  static async updateUserProfile(firebaseUid, userData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}users/${firebaseUid}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update user profile");
      }

      return await response.json();
    } catch (error) {
      console.error("API Update Profile Error:", error);
      throw error;
    }
  }

  // ===== COMMUNITY API METHODS =====

  // Residency methods
  static async getResidencies() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}residencies`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch residencies");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Residencies Error:", error);
      throw error;
    }
  }
  static async createResidency(residencyData) {
    try {
      const authHeaders = await this.getAuthHeaders();

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("name", residencyData.name);

      if (residencyData.image) {
        formData.append("image", residencyData.image);
      }

      // Note: Don't set Content-Type header for FormData, let browser set it
      const response = await fetch(`${API_BASE_URL}residencies`, {
        method: "POST",
        headers: {
          Authorization: authHeaders.Authorization,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create residency");
      }

      return await response.json();
    } catch (error) {
      console.error("API Create Residency Error:", error);
      throw error;
    }
  }
  static async updateResidency(residencyId, residencyData) {
    try {
      const user = auth.currentUser;
      const headers = {};

      if (user) {
        const token = await user.getIdToken();
        headers.Authorization = `Bearer ${token}`;
      }

      // Handle FormData for file uploads
      let body;
      if (residencyData instanceof FormData) {
        body = residencyData;
        // Don't set Content-Type for FormData, let browser set it with boundary
      } else {
        headers["Content-Type"] = "application/json";
        body = JSON.stringify(residencyData);
      }

      const response = await fetch(
        `${API_BASE_URL}residencies/${residencyId}`,
        {
          method: "PUT",
          headers,
          body,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update residency");
      }

      return await response.json();
    } catch (error) {
      console.error("API Update Residency Error:", error);
      throw error;
    }
  }

  static async deleteResidency(residencyId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}residencies/${residencyId}`,
        {
          method: "DELETE",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete residency");
      }

      return await response.json();
    } catch (error) {
      console.error("API Delete Residency Error:", error);
      throw error;
    }
  }

  // Block methods
  static async getBlocks(residencyId = null) {
    try {
      const headers = await this.getAuthHeaders();
      const url = residencyId
        ? `${API_BASE_URL}blocks?residencyId=${residencyId}`
        : `${API_BASE_URL}blocks`;

      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch blocks");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Blocks Error:", error);
      throw error;
    }
  }

  static async createBlock(blockData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}blocks`, {
        method: "POST",
        headers,
        body: JSON.stringify(blockData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create block");
      }

      return await response.json();
    } catch (error) {
      console.error("API Create Block Error:", error);
      throw error;
    }
  }

  static async updateBlock(blockId, blockData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}blocks/${blockId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(blockData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update block");
      }

      return await response.json();
    } catch (error) {
      console.error("API Update Block Error:", error);
      throw error;
    }
  }

  static async deleteBlock(blockId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}blocks/${blockId}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete block");
      }

      return await response.json();
    } catch (error) {
      console.error("API Delete Block Error:", error);
      throw error;
    }
  }

  // Flat methods
  static async getFlats(blockId = null) {
    try {
      const headers = await this.getAuthHeaders();
      const url = blockId
        ? `${API_BASE_URL}flats?blockId=${blockId}`
        : `${API_BASE_URL}flats`;

      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch flats");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Flats Error:", error);
      throw error;
    }
  }

  static async createFlat(flatData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}flats`, {
        method: "POST",
        headers,
        body: JSON.stringify(flatData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create flat");
      }

      return await response.json();
    } catch (error) {
      console.error("API Create Flat Error:", error);
      throw error;
    }
  }

  static async updateFlat(flatId, flatData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}flats/${flatId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(flatData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update flat");
      }

      return await response.json();
    } catch (error) {
      console.error("API Update Flat Error:", error);
      throw error;
    }
  }

  static async deleteFlat(flatId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}flats/${flatId}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete flat");
      }

      return await response.json();
    } catch (error) {
      console.error("API Delete Flat Error:", error);
      throw error;
    }
  }

  // ===== USER MANAGEMENT API METHODS =====

  // Get all users (admin only)
  static async getAllUsers() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}users`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch users");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Users Error:", error);
      throw error;
    }
  }

  // Update user community assignment (admin only)
  static async updateUserCommunity(userId, communityData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}users/${userId}/community`, {
        method: "PUT",
        headers,
        body: JSON.stringify(communityData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to update user community assignment"
        );
      }
      return await response.json();
    } catch (error) {
      console.error("API Update User Community Error:", error);
      throw error;
    }
  }

  // Delete user (admin only)
  static async deleteUser(userId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}users/${userId}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete user");
      }

      return await response.json();
    } catch (error) {
      console.error("API Delete User Error:", error);
      throw error;
    }
  }
  // Update user profile with community fields
  static async updateUserProfileCommunity(firebaseUid, communityData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}users/${firebaseUid}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(communityData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to update user profile community"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Update User Profile Community Error:", error);
      throw error;
    }
  }

  // ===== USER PROFILE ONBOARDING API METHODS =====
  // Get user onboarding status
  static async getOnboardingStatus() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL.replace(
          "/api/v1/",
          "/api/"
        )}user-profiles/onboarding/status`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to get onboarding status");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Onboarding Status Error:", error);
      throw error;
    }
  }
  // Accept terms and conditions
  static async acceptTerms() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL.replace(
          "/api/v1/",
          "/api/"
        )}user-profiles/terms/accept`,
        {
          method: "POST",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to accept terms");
      }

      return await response.json();
    } catch (error) {
      console.error("API Accept Terms Error:", error);
      throw error;
    }
  }
  // Check terms acceptance status
  static async checkTermsAcceptance() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL.replace("/api/v1/", "/api/")}user-profiles/terms/check`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to check terms acceptance"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Check Terms Acceptance Error:", error);
      throw error;
    }
  }
  // Update user profile details
  static async updateUserProfileDetails(userId, profileData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}user-profiles/${userId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to update profile details"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Update Profile Details Error:", error);
      throw error;
    }
  }

  // Car Types API Methods
  // Get all car types
  static async getCarTypes(includeInactive = false) {
    try {
      const url = `${API_BASE_URL}car-types${
        includeInactive ? "?includeInactive=true" : ""
      }`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch car types");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Car Types Error:", error);
      throw error;
    }
  }

  // Alias for compatibility
  static async getAllCarTypes(includeInactive = false) {
    return this.getCarTypes(includeInactive);
  }

  // Get car type by ID
  static async getCarTypeById(id) {
    try {
      const response = await fetch(`${API_BASE_URL}car-types/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch car type");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Car Type By ID Error:", error);
      throw error;
    }
  }
  // Search car types by name
  static async searchCarTypesByName(name) {
    try {
      const response = await fetch(
        `${API_BASE_URL}car-types/search/${encodeURIComponent(name)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to search car types by name"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Search Car Types By Name Error:", error);
      throw error;
    }
  }

  // Create new car type (admin only)
  static async createCarType(carTypeData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}car-types`, {
        method: "POST",
        headers,
        body: JSON.stringify(carTypeData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create car type");
      }

      return await response.json();
    } catch (error) {
      console.error("API Create Car Type Error:", error);
      throw error;
    }
  }

  // Update car type (admin only)
  static async updateCarType(id, carTypeData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}car-types/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(carTypeData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update car type");
      }

      return await response.json();
    } catch (error) {
      console.error("API Update Car Type Error:", error);
      throw error;
    }
  }

  // Delete car type (admin only)
  static async deleteCarType(id) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}car-types/${id}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete car type");
      }

      return await response.json();
    } catch (error) {
      console.error("API Delete Car Type Error:", error);
      throw error;
    }
  }

  // Restore car type (admin only)
  static async restoreCarType(id) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}car-types/${id}/restore`, {
        method: "POST",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to restore car type");
      }
      return await response.json();
    } catch (error) {
      console.error("API Restore Car Type Error:", error);
      throw error;
    }
  }
  // Contact Form Submission methods
  static async submitContactForm(formData) {
    try {
      // No authentication required for public contact form
      const response = await fetch(`${API_BASE_URL}contact-submissions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit contact form");
      }

      return await response.json();
    } catch (error) {
      console.error("API Submit Contact Form Error:", error);
      throw error;
    }
  }

  // ===== PRICING PLAN API METHODS =====

  // Get all pricing plans
  static async getPricingPlans(includeInactive = false) {
    try {
      const headers = await this.getAuthHeaders();
      const url = includeInactive
        ? `${API_BASE_URL}pricing-plans?includeInactive=true`
        : `${API_BASE_URL}pricing-plans`;

      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch pricing plans");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Pricing Plans Error:", error);
      throw error;
    }
  }

  // Get pricing plan by ID
  static async getPricingPlanById(id) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}pricing-plans/${id}`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch pricing plan");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Pricing Plan Error:", error);
      throw error;
    }
  }

  // Get pricing plan with all car types and pricing
  static async getPricingPlanWithCarTypes(id) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}pricing-plans/${id}/with-car-types`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch pricing plan with car types"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Pricing Plan With Car Types Error:", error);
      throw error;
    }
  }

  // Create new pricing plan (admin only)
  static async createPricingPlan(planData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}pricing-plans`, {
        method: "POST",
        headers,
        body: JSON.stringify(planData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create pricing plan");
      }

      return await response.json();
    } catch (error) {
      console.error("API Create Pricing Plan Error:", error);
      throw error;
    }
  }

  // Update pricing plan (admin only)
  static async updatePricingPlan(id, planData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}pricing-plans/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(planData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update pricing plan");
      }

      return await response.json();
    } catch (error) {
      console.error("API Update Pricing Plan Error:", error);
      throw error;
    }
  }

  // Delete pricing plan (admin only)
  static async deletePricingPlan(id) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}pricing-plans/${id}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete pricing plan");
      }

      return await response.json();
    } catch (error) {
      console.error("API Delete Pricing Plan Error:", error);
      throw error;
    }
  }

  // Restore pricing plan (admin only)
  static async restorePricingPlan(id) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}pricing-plans/${id}/restore`,
        {
          method: "POST",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to restore pricing plan");
      }

      return await response.json();
    } catch (error) {
      console.error("API Restore Pricing Plan Error:", error);
      throw error;
    }
  }

  // Bulk update pricing for all car types in a pricing plan (admin only)
  static async bulkUpdatePricing(id, carTypePricing) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}pricing-plans/${id}/bulk-pricing`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({ carTypePricing }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update pricing");
      }

      return await response.json();
    } catch (error) {
      console.error("API Bulk Update Pricing Error:", error);
      throw error;
    }
  }

  // Get pricing plan statistics (admin only)
  static async getPricingPlanStats() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}pricing-plans/admin/stats`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch pricing plan statistics"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Pricing Plan Stats Error:", error);
      throw error;
    }
  }

  // Purchase API methods
  static async createPurchase(purchaseData) {
    try {
      const response = await fetch(`${API_BASE_URL}purchases`, {
        method: "POST",
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(purchaseData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create purchase");
      }

      return await response.json();
    } catch (error) {
      console.error("API Create Purchase Error:", error);
      throw error;
    }
  }

  static async completePurchase(purchaseId, completionData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}purchases/${purchaseId}/complete`,
        {
          method: "PUT",
          headers: await this.getAuthHeaders(),
          body: JSON.stringify(completionData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to complete purchase");
      }

      return await response.json();
    } catch (error) {
      console.error("API Complete Purchase Error:", error);
      throw error;
    }
  }

  static async getUserPurchases() {
    try {
      const response = await fetch(`${API_BASE_URL}purchases/user`, {
        method: "GET",
        headers: await this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch purchases");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get User Purchases Error:", error);
      throw error;
    }
  }

  static async getPurchase(purchaseId) {
    try {
      const response = await fetch(`${API_BASE_URL}purchases/${purchaseId}`, {
        method: "GET",
        headers: await this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch purchase");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Purchase Error:", error);
      throw error;
    }
  }

  // Booking API methods
  static async createBooking(bookingData) {
    try {
      const response = await fetch(`${API_BASE_URL}bookings`, {
        method: "POST",
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create booking");
      }

      return await response.json();
    } catch (error) {
      console.error("API Create Booking Error:", error);
      throw error;
    }
  }

  static async getUserBookings() {
    try {
      const response = await fetch(`${API_BASE_URL}bookings/user`, {
        method: "GET",
        headers: await this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch bookings");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get User Bookings Error:", error);
      throw error;
    }
  }

  static async cancelBooking(bookingId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}bookings/${bookingId}/cancel`,
        {
          method: "PUT",
          headers: await this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to cancel booking");
      }

      return await response.json();
    } catch (error) {
      console.error("API Cancel Booking Error:", error);
      throw error;
    }
  }
  static async getAvailableSlots(date) {
    try {
      const response = await fetch(
        `${API_BASE_URL}bookings/available-slots?date=${date}`,
        {
          method: "GET",
          headers: await this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch available slots");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Available Slots Error:", error);
      throw error;
    }
  }
  static async checkUserBookingExists(slotId, date) {
    try {
      const url = `${API_BASE_URL}bookings/check-booking-exists?slotId=${slotId}&date=${date}`;
      console.log("Making API request to:", url);

      const headers = await this.getAuthHeaders();
      console.log("Request headers:", headers);

      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);

      if (!response.ok) {
        const errorData = await response.json();
        console.error("API Error:", errorData);
        throw new Error(errorData.message || "Failed to check booking");
      }

      const result = await response.json();
      console.log("API Result:", result);
      return result;
    } catch (error) {
      console.error("API Check Booking Error:", error);
      throw error;
    }
  }

  // Booking Slot API methods
  static async getAllBookingSlots() {
    try {
      const response = await fetch(`${API_BASE_URL}booking-slots`, {
        method: "GET",
        headers: await this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch booking slots");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Booking Slots Error:", error);
      throw error;
    }
  }
  static async getAvailableSlotsForDate(date) {
    try {
      const response = await fetch(
        `${API_BASE_URL}booking-slots/available/${date}`,
        {
          method: "GET",
          headers: await this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch available slots for date"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Available Slots for Date Error:", error);
      throw error;
    }
  }

  static async createBookingSlot(slotData) {
    try {
      const response = await fetch(`${API_BASE_URL}booking-slots`, {
        method: "POST",
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(slotData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create booking slot");
      }

      return await response.json();
    } catch (error) {
      console.error("API Create Booking Slot Error:", error);
      throw error;
    }
  }

  static async updateBookingSlot(id, slotData) {
    try {
      const response = await fetch(`${API_BASE_URL}booking-slots/${id}`, {
        method: "PUT",
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(slotData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update booking slot");
      }

      return await response.json();
    } catch (error) {
      console.error("API Update Booking Slot Error:", error);
      throw error;
    }
  }

  static async deleteBookingSlot(id) {
    try {
      const response = await fetch(`${API_BASE_URL}booking-slots/${id}`, {
        method: "DELETE",
        headers: await this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete booking slot");
      }

      return await response.json();
    } catch (error) {
      console.error("API Delete Booking Slot Error:", error);
      throw error;
    }
  }

  static async getBookingSlot(id) {
    try {
      const response = await fetch(`${API_BASE_URL}booking-slots/${id}`, {
        method: "GET",
        headers: await this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch booking slot");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Booking Slot Error:", error);
      throw error;
    }
  }

  static async updateSlotsOrder(slotsOrder) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}booking-slots/order`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ slotsOrder }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update slots order");
      }

      return await response.json();
    } catch (error) {
      console.error("API Update Slots Order Error:", error);
      throw error;
    }
  }

  // User Subscription API methods
  static async getUserSubscriptions() {
    try {
      const response = await fetch(`${API_BASE_URL}subscriptions/user`, {
        method: "GET",
        headers: await this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch subscriptions");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get User Subscriptions Error:", error);
      throw error;
    }
  }

  static async getActiveUserSubscriptions() {
    try {
      const response = await fetch(`${API_BASE_URL}subscriptions/user/active`, {
        method: "GET",
        headers: await this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch active subscriptions"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Active User Subscriptions Error:", error);
      throw error;
    }
  }

  // ===== ADMIN BOOKING MANAGEMENT API METHODS =====

  // Get all bookings (admin only)
  static async getAllBookings(
    page = 1,
    limit = 20,
    search = "",
    status = "",
    startDate = "",
    endDate = ""
  ) {
    try {
      const headers = await this.getAuthHeaders();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(status && { status }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      });

      const response = await fetch(
        `${API_BASE_URL}bookings/admin/all?${params}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch all bookings");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get All Bookings Error:", error);
      throw error;
    }
  }

  // Get bookings by date (admin only)
  static async getBookingsByDate(date) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}bookings/admin/by-date?date=${date}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch bookings by date"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Bookings By Date Error:", error);
      throw error;
    }
  }

  // Get bookings by date range (admin only)
  static async getBookingsByDateRange(startDate, endDate) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}bookings/admin/by-date-range?startDate=${startDate}&endDate=${endDate}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch bookings by date range"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Bookings By Date Range Error:", error);
      throw error;
    }
  }

  // Update booking status (admin only)
  static async updateBookingStatus(bookingId, status, notes = null) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}bookings/admin/${bookingId}/status`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({ status, notes }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update booking status");
      }

      return await response.json();
    } catch (error) {
      console.error("API Update Booking Status Error:", error);
      throw error;
    }
  }

  // Get booking statistics (admin only)
  static async getBookingStatistics(startDate, endDate) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}bookings/admin/statistics?startDate=${startDate}&endDate=${endDate}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch booking statistics"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Booking Statistics Error:", error);
      throw error;
    }
  }
  // Get single booking details (admin only)
  static async getBookingById(bookingId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}bookings/${bookingId}`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch booking details");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Booking By ID Error:", error);
      throw error;
    }
  }

  // ===== ADMIN USER SUBSCRIPTION MANAGEMENT API METHODS =====

  // Get all subscriptions (admin only)
  static async getAllSubscriptions(isActive = null, userFirebaseUid = null) {
    try {
      const headers = await this.getAuthHeaders();
      const params = new URLSearchParams();

      if (isActive !== null) {
        params.append("isActive", isActive.toString());
      }
      if (userFirebaseUid) {
        params.append("userFirebaseUid", userFirebaseUid);
      }

      const response = await fetch(`${API_BASE_URL}subscriptions?${params}`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch subscriptions");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get All Subscriptions Error:", error);
      throw error;
    }
  }
  // Get active subscriptions for a specific user (admin only)
  static async getActiveSubscriptionsForUser(userFirebaseUid) {
    try {
      return await this.getAllSubscriptions(true, userFirebaseUid);
    } catch (error) {
      console.error("API Get Active Subscriptions For User Error:", error);
      throw error;
    }
  }

  // ===== DISABLED DATES MANAGEMENT API METHODS =====

  // Get upcoming disabled dates (public)
  static async getUpcomingDisabledDates() {
    try {
      const response = await fetch(`${API_BASE_URL}disabled-dates/upcoming`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch disabled dates");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Upcoming Disabled Dates Error:", error);
      throw error;
    }
  }

  // Check if specific date is disabled (public)
  static async checkDateDisabled(date) {
    try {
      const response = await fetch(
        `${API_BASE_URL}disabled-dates/check/${date}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to check date status");
      }

      return await response.json();
    } catch (error) {
      console.error("API Check Date Disabled Error:", error);
      throw error;
    }
  }

  // Get all disabled dates (admin only)
  static async getAllDisabledDates() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}disabled-dates`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch disabled dates");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get All Disabled Dates Error:", error);
      throw error;
    }
  }

  // Get disabled dates by date range (admin only)
  static async getDisabledDatesByRange(startDate, endDate) {
    try {
      const headers = await this.getAuthHeaders();
      const params = new URLSearchParams({
        startDate,
        endDate,
      });

      const response = await fetch(
        `${API_BASE_URL}disabled-dates/range?${params}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch disabled dates by range"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Disabled Dates By Range Error:", error);
      throw error;
    }
  }

  // Create disabled date (admin only)
  static async createDisabledDate(dateData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}disabled-dates`, {
        method: "POST",
        headers,
        body: JSON.stringify(dateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create disabled date");
      }

      return await response.json();
    } catch (error) {
      console.error("API Create Disabled Date Error:", error);
      throw error;
    }
  }

  // Update disabled date (admin only)
  static async updateDisabledDate(id, dateData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}disabled-dates/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(dateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update disabled date");
      }

      return await response.json();
    } catch (error) {
      console.error("API Update Disabled Date Error:", error);
      throw error;
    }
  }

  // Delete (enable) disabled date (admin only)
  static async deleteDisabledDate(id) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}disabled-dates/${id}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete disabled date");
      }
      return await response.json();
    } catch (error) {
      console.error("API Delete Disabled Date Error:", error);
      throw error;
    }
  }

  // ===== COUPON MANAGEMENT API METHODS =====

  // Validate coupon code (public - used during purchase)
  static async validateCoupon(couponCode) {
    try {
      const response = await fetch(
        `${API_BASE_URL}coupons/validate/${encodeURIComponent(
          couponCode.toUpperCase()
        )}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to validate coupon");
      }

      return await response.json();
    } catch (error) {
      console.error("API Validate Coupon Error:", error);
      throw error;
    }
  }

  // Get all coupons (admin only)
  static async getAllCoupons(
    page = 1,
    limit = 10,
    search = "",
    isActive = null
  ) {
    try {
      const headers = await this.getAuthHeaders();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(isActive !== null && { isActive: isActive.toString() }),
      });

      const response = await fetch(`${API_BASE_URL}coupons?${params}`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch coupons");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get All Coupons Error:", error);
      throw error;
    }
  }

  // Get coupon by ID (admin only)
  static async getCouponById(id) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}coupons/${id}`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch coupon");
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Coupon By ID Error:", error);
      throw error;
    }
  }

  // Create new coupon (admin only)
  static async createCoupon(couponData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}coupons`, {
        method: "POST",
        headers,
        body: JSON.stringify(couponData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create coupon");
      }

      return await response.json();
    } catch (error) {
      console.error("API Create Coupon Error:", error);
      throw error;
    }
  }

  // Update coupon (admin only)
  static async updateCoupon(id, couponData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}coupons/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(couponData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update coupon");
      }

      return await response.json();
    } catch (error) {
      console.error("API Update Coupon Error:", error);
      throw error;
    }
  }

  // Delete coupon (admin only)
  static async deleteCoupon(id) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}coupons/${id}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete coupon");
      }

      return await response.json();
    } catch (error) {
      console.error("API Delete Coupon Error:", error);
      throw error;
    }
  }

  // Get coupon statistics (admin only)
  static async getCouponStats() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}coupons/admin/stats`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch coupon statistics"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API Get Coupon Stats Error:", error);
      throw error;
    }
  }
}

export default ApiService;
